// Agent-domain slice: agent CRUD, tool catalog, workflow options, sidebar
// activity tracking, and chat-attachment / avatar uploads.
//
// Composed into useAgentStore via slice composition (single create()).
// Cross-slice calls go through get(), which resolves to the full store.

import {
  send_request,
} from "../../utils/Request";
import {
  _getClientId,
  extractApiStatusCode,
  resolveApiErrorMessage,
  resolveAgentMutationError,
  inferMimeTypeFromFileName,
  _isSidebarActiveSessionStatus,
  _buildAgentActivityFromSessions,
  _bumpTeamLastActivityPatch,
  _activityMsFromPayload,
  _bumpAgentLastActivityPatch,
  _isKnownStreamableSessionStatus,
} from "./agentStoreUtils";
import useChatStreamStore from "./useChatStreamStore";
import config from "../../config";

async function _applyAgentAvatarMutation(
  set,
  { agentId, body, method, headers, missingIdMessage, failureMessage }
) {
  const normalizedAgentId = String(agentId || "").trim();
  if (!normalizedAgentId) throw new Error(missingIdMessage);

  const response = await send_request(
    `project-service/agent/${normalizedAgentId}/avatar`,
    body,
    "",
    method,
    headers
  );

  const updated = response?.data || null;
  if (!updated?.agentId) {
    const message = resolveApiErrorMessage(response, failureMessage);
    set({ error: message });
    throw new Error(message);
  }

  set((state) => ({
    agents: state.agents.map((agent) =>
      agent.agentId === normalizedAgentId ? { ...agent, ...updated } : agent
    ),
  }));
  return updated;
}

export function createAgentsSlice(set, get) {
  return {
  agents: [],
  loading: false,
  toolCatalog: [],
  /** Map<agentId, boolean> — true when the agent has a session actively executing (DM sidebar dots) */
  agentHasActiveSession: {},
  /** Map<agentId, number> — latest session activity timestamp (ms) for sidebar ordering */
  agentLastActivityAt: {},
  /** Map<teamId, number> — latest team session activity timestamp (ms) for sidebar ordering */
  teamLastActivityAt: {},
  /** False until recent-sessions bootstrap completes so sidebar can render final order */
  agentSidebarOrderReady: false,

  /**
   * Bootstrap agent activity with a single bulk fetch via recent-sessions.
   */
  bootstrapAgentActivity: async () => {
    const clientId = _getClientId();
    set({ agentSidebarOrderReady: false });
    if (!clientId) {
      set({ agentSidebarOrderReady: true });
      return;
    }
    try {
      const response = await send_request(
        "ai-service/ai/agent/recent-sessions",
        "",
        { clientId, limit: 50 },
        "GET"
      );
      if (response instanceof Error || !response?.data) {
        set({ agentSidebarOrderReady: true });
        return;
      }
      const sessions = Array.isArray(response.data) ? response.data : [];
      const { activeMap, lastActivityMap, teamLastActivityMap } =
        _buildAgentActivityFromSessions(sessions);
      set({
        agentHasActiveSession: activeMap,
        agentLastActivityAt: lastActivityMap,
        teamLastActivityAt: teamLastActivityMap,
        agentSidebarOrderReady: true,
      });
    } catch (_error) {
      set({ agentSidebarOrderReady: true });
    }
  },

  /**
   * Handle an SSE activity event from the agent channel.
   * Updates agentHasActiveSession and agentLastActivityAt based on session lifecycle events.
   */
  handleAgentActivityEvent: (agentId, eventType, payload) => {
    if (eventType === "new_session") {
      set((s) => ({
        agentHasActiveSession: { ...s.agentHasActiveSession, [agentId]: true },
        ...(_bumpAgentLastActivityPatch(
          s,
          agentId,
          _activityMsFromPayload(payload)
        ) || {}),
      }));
      return;
    }

    // session_activity carries wrapped payload from SSE:
    // { streamVersion, type, sessionId, payload: { originalEventType, status, ... } }
    const inner = payload?.payload ?? payload;
    const originalType = inner?.originalEventType;
    const newStatus = inner?.status;
    const activitySessionId = payload?.sessionId ?? inner?.sessionId ?? null;

    // Keep the OPEN conversation in sync when its session changes out-of-band.
    // The session SSE stream closes when a turn completes, so an inter-agent
    // reply that reopens (and often quickly re-completes) the currently-open
    // session never reaches the conversation view — only the sidebar status
    // updates. The session-level SSE channel has no live subscriber to deliver
    // the new message_added, and reopenReactivatedSessionStream alone is
    // unreliable here because the reactivated turn can finish before we ever
    // observe a streamable status. So: if the session is live again,
    // resubscribe to stream the turn; otherwise pull the latest messages
    // directly so the reply appears without a manual refresh.
    if (
      (originalType === "status_changed" || originalType === "session_complete")
      && activitySessionId
      && get().sessionDetail?.chatSessionId === activitySessionId
    ) {
      const isLive =
        useChatStreamStore.getState().getSubscribedSessionId()
        === activitySessionId;
      if (!isLive) {
        if (
          typeof newStatus === "string"
          && _isKnownStreamableSessionStatus(newStatus)
        ) {
          get().reopenReactivatedSessionStream(activitySessionId, newStatus);
        } else {
          // Non-forced on purpose: when the per-session stream delivered this
          // completion (normal turn), the POST_COMPLETE guard suppresses this
          // fetch so a partial REST response cannot overwrite the live final
          // message. When the turn ran out-of-band (no per-session subscriber)
          // the guard was never armed, so the fetch proceeds and pulls the
          // messages we missed.
          get().fetchSessionDetail(activitySessionId, { silent: true });
        }
      }
    }

    if (originalType === "status_changed" && typeof newStatus === "string") {
      const active = _isSidebarActiveSessionStatus(newStatus);
      set((s) => ({
        agentHasActiveSession: {
          ...s.agentHasActiveSession,
          [agentId]: active,
        },
        ...(_bumpAgentLastActivityPatch(
          s,
          agentId,
          _activityMsFromPayload(payload)
        ) || {}),
      }));
      return;
    }

    // session_complete — mark inactive. If another session on this agent is still
    // running, its own status_changed/new_session events will re-activate.
    if (originalType === "session_complete") {
      set((s) => ({
        agentHasActiveSession: { ...s.agentHasActiveSession, [agentId]: false },
        ...(_bumpAgentLastActivityPatch(
          s,
          agentId,
          _activityMsFromPayload(payload)
        ) || {}),
      }));
    }
  },

  bumpAgentLastActivity: (agentId, timestampMs = Date.now()) => {
    if (!agentId) return;
    set((s) => _bumpAgentLastActivityPatch(s, agentId, timestampMs) || s);
  },

  bumpTeamLastActivity: (teamId, timestampMs = Date.now()) => {
    if (!teamId) return;
    set((s) => _bumpTeamLastActivityPatch(s, teamId, timestampMs) || s);
  },

  ensureAgentsLoaded: async () => {
    const { agents, loading, fetchAgents } = get();
    if (agents.length > 0 || loading) {
      return;
    }
    await fetchAgents();
  },

  fetchAgents: async () => {
    set({ loading: true, error: null, agentSidebarOrderReady: false });
    try {
      const response = await send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      );
      const nextAgents = response?.data || [];
      const hasSidebarAgents = nextAgents.length > 0;
      set({
        agents: nextAgents,
        loading: false,
        agentSidebarOrderReady: !hasSidebarAgents,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load agents",
        loading: false,
        agentSidebarOrderReady: true,
      });
    }
  },

  /** Silently refresh agents list without triggering loading skeleton. */
  refreshAgents: async () => {
    try {
      const response = await send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      );
      set({ agents: response?.data || [] });
    } catch (_e) {
      // silent — keeps existing list on failure
    }
  },

  createAgent: async (agentData) => {
    try {
      const response = await send_request(
        "project-service/agent/create",
        agentData,
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => ({ agents: [...s.agents, response.data] }));
        return response.data;
      }
      // send_request returns the error instead of throwing — extract server message
      set({ error: resolveAgentMutationError(response, "Failed to create agent") });
      return null;
    } catch (e) {
      set({ error: resolveAgentMutationError(e, "Failed to create agent") });
      return null;
    }
  },

  updateAgent: async (agentId, agentData) => {
    try {
      const response = await send_request(
        `project-service/agent/${agentId}`,
        agentData,
        {},
        "PUT"
      );
      if (response?.data) {
        set((s) => {
          const agents = [...s.agents];
          const index = agents.findIndex((a) => a.agentId === agentId);
          if (index >= 0) agents[index] = response.data;
          return { agents };
        });
        return response.data;
      }
      // send_request returns the error instead of throwing — extract server message
      set({ error: resolveAgentMutationError(response, "Failed to update agent") });
      return null;
    } catch (e) {
      set({ error: resolveAgentMutationError(e, "Failed to update agent") });
      return null;
    }
  },

  fetchAgent: async (agentId) => {
    try {
      const response = await send_request(
        `project-service/agent/${agentId}`,
        "",
        {},
        "GET"
      );
      return response?.data || null;
    } catch (e) {
      set({ error: e.message || "Failed to load agent" });
      return null;
    }
  },

  fetchToolCatalog: async () => {
    try {
      const clientId = _getClientId();
      const response = await send_request(
        "project-service/agent/tools/catalog",
        "",
        clientId ? { clientId } : {},
        "GET"
      );
      const catalog = response?.data;
      const toolCatalog = Array.isArray(catalog) ? catalog : [];
      set({ toolCatalog });
      return toolCatalog;
    } catch (e) {
      set({
        error: e.message || "Failed to load tool catalog",
        toolCatalog: [],
      });
      return [];
    }
  },

  fetchWorkflowOptions: async () => {
    try {
      const response = await send_request(
        "project-service/agent/workflow-options",
        "",
        {},
        "GET"
      );
      const options = response?.data;
      return Array.isArray(options) ? options : [];
    } catch (e) {
      set({ error: e.message || "Failed to load workflow options" });
      return [];
    }
  },

  deleteAgent: async (agentId) => {
    try {
      await send_request(`project-service/agent/${agentId}`, "", {}, "DELETE");
      set((s) => ({
        agents: s.agents.filter((a) => a.agentId !== agentId),
      }));
      return true;
    } catch (e) {
      set({ error: e.message || "Failed to delete agent" });
      return false;
    }
  },

  saveEditedAgentFile: async ({
    agentId,
    previousFileId,
    fileName,
    content,
    mimeType,
  }) => {
    const normalizedAgentId = String(agentId || "").trim();
    const normalizedFileName = String(fileName || "").trim() || "document.txt";
    const normalizedMimeType =
      String(mimeType || "").trim() ||
      inferMimeTypeFromFileName(normalizedFileName);

    if (!normalizedAgentId) {
      const message = "Missing agent ID for file save";
      set({ error: message });
      throw new Error(message);
    }

    const formData = new FormData();
    formData.append(
      "file",
      new Blob([content ?? ""], { type: normalizedMimeType }),
      normalizedFileName
    );
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("agentId", normalizedAgentId);

    try {
      const uploadResponse = await send_request(
        "project-service/project/file-library/create",
        formData,
        "",
        "POST",
        { "content-type": "multipart/form-data" }
      );

      const uploaded = uploadResponse?.data || null;
      const uploadedFileId = String(uploaded?.id || "").trim();
      if (!uploadedFileId) {
        const message = resolveApiErrorMessage(
          uploadResponse,
          "Failed to save edited file"
        );
        set({ error: message });
        throw new Error(message);
      }

      let warning = "";
      const normalizedPreviousId = String(previousFileId || "").trim();
      if (normalizedPreviousId && normalizedPreviousId !== uploadedFileId) {
        const deleteResponse = await send_request(
          `project-service/project/file-library/agent/${normalizedAgentId}/deletes`,
          { deleteList: [normalizedPreviousId] },
          "",
          "POST"
        );

        const deleteFailed = extractApiStatusCode(deleteResponse) >= 400;
        if (deleteFailed) {
          warning = resolveApiErrorMessage(
            deleteResponse,
            "Updated file saved, but old file deletion failed"
          );
        }
      }

      return {
        fileId: uploadedFileId,
        fileName: uploaded?.fileName || normalizedFileName,
        downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
        mimeType:
          uploaded?.mimeType || uploaded?.fileType || normalizedMimeType,
        size: uploaded?.fileSize ?? null,
        createdAt: uploaded?.createDate || null,
        agentId: normalizedAgentId,
        warning,
      };
    } catch (e) {
      const message = e?.message || "Failed to save edited file";
      set({ error: message });
      throw e instanceof Error ? e : new Error(message);
    }
  },

  uploadChatAttachment: async (file, agentId) => {
    const normalizedAgentId = String(agentId || "").trim();
    if (!normalizedAgentId) throw new Error("Missing agent ID for file upload");

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("agentId", normalizedAgentId);

    const uploadResponse = await send_request(
      "project-service/project/file-library/create",
      formData,
      "",
      "POST",
      { "content-type": "multipart/form-data" }
    );

    const uploaded = uploadResponse?.data || null;
    const uploadedFileId = String(uploaded?.id || "").trim();
    if (!uploadedFileId) {
      throw new Error("Failed to upload file");
    }

    return {
      fileId: uploadedFileId,
      fileName: uploaded?.fileName || file.name,
      downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
      mimeType: uploaded?.mimeType || uploaded?.fileType || file.type,
      size: uploaded?.fileSize ?? file.size,
    };
  },

  uploadAgentAvatar: async (agentId, file) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    return _applyAgentAvatarMutation(set, {
      agentId,
      body: formData,
      method: "POST",
      headers: { "content-type": "multipart/form-data" },
      missingIdMessage: "Missing agent ID for avatar upload",
      failureMessage: "Failed to upload avatar",
    });
  },

  clearAgentAvatar: async (agentId) => {
    return _applyAgentAvatarMutation(set, {
      agentId,
      body: {},
      method: "DELETE",
      missingIdMessage: "Missing agent ID for avatar removal",
      failureMessage: "Failed to remove avatar",
    });
  },

  deleteChatAttachment: async (fileId, agentId) => {
    const normalizedAgentId = String(agentId || "").trim();
    const normalizedFileId = String(fileId || "").trim();
    if (!normalizedAgentId || !normalizedFileId) return;

    await send_request(
      `project-service/project/file-library/agent/${normalizedAgentId}/deletes`,
      { deleteList: [normalizedFileId] },
      "",
      "POST"
    );
  },
  };
}
